import { ChevronUp, ChevronDown, Copy, Trash2, Plus, X } from "lucide-react";
import { FIELD_TYPES, SUB_FIELD_TYPES, OPTION_TYPES, LAYOUT_TYPES, blankField } from "../../lib/formFieldTypes";
import { FormsRichTextEditor } from "./FormsRichTextEditor";

const inputCls = "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal/30";

/**
 * One field's admin editor controls (used for both top-level template
 * fields and a repeatable_group's sub_fields, via `typeOptions`).
 *
 * NOTE: the repeatable-group sub-row editor is rendered via a separate
 * `SubFieldEditorRow` function (not by calling this component by its
 * own name) — a genuinely self-referencing exported component tripped
 * a webpack/react-refresh "Maximum call stack size exceeded" build
 * error in this dev setup, even though sub_fields can never itself be
 * type "repeatable_group" (see SUB_FIELD_TYPES), so no real recursion
 * ever occurs at runtime.
 */
function FieldEditorRowImpl({ field, index, total, onUpdate, onRemove, onDuplicate, onMove, typeOptions = FIELD_TYPES, testIdPrefix = "field", SubRowComponent }) {
  const isLayout = LAYOUT_TYPES.has(field.type);
  const hasOptions = OPTION_TYPES.has(field.type);
  const isRepeatable = field.type === "repeatable_group";

  const set = (patch) => onUpdate({ ...field, ...patch });

  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white" data-testid={`${testIdPrefix}-${field.field_id}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <select
          value={field.type}
          onChange={(e) => {
            const next = blankField(e.target.value);
            set({ type: next.type, options: next.options, sub_fields: next.sub_fields, required: false });
          }}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-medium text-ink-muted"
          data-testid={`${testIdPrefix}-${field.field_id}-type-select`}
        >
          {typeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div className="flex items-center gap-1">
          {onMove && (
            <>
              <button type="button" disabled={index === 0} onClick={() => onMove(index, index - 1)}
                      className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`${testIdPrefix}-${field.field_id}-move-up`}>
                <ChevronUp className="w-4 h-4" />
              </button>
              <button type="button" disabled={index === total - 1} onClick={() => onMove(index, index + 1)}
                      className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`${testIdPrefix}-${field.field_id}-move-down`}>
                <ChevronDown className="w-4 h-4" />
              </button>
            </>
          )}
          {onDuplicate && (
            <button type="button" onClick={onDuplicate} className="p-1.5 rounded hover:bg-slate-100" data-testid={`${testIdPrefix}-${field.field_id}-duplicate`}>
              <Copy className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={onRemove} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`${testIdPrefix}-${field.field_id}-remove`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-medium text-ink-muted">{isLayout ? "Heading / text" : "Question / label"}</span>
        <input value={field.label} onChange={(e) => set({ label: e.target.value })} className={inputCls}
               data-testid={`${testIdPrefix}-${field.field_id}-label-input`} />
      </label>

      {field.type === "text_block" && (
        <label className="block mt-2">
          <span className="text-xs font-medium text-ink-muted">Explanatory text shown to members</span>
          <FormsRichTextEditor value={field.help_text} onChange={(v) => set({ help_text: v })}
                                testId={`${testIdPrefix}-${field.field_id}-helptext-input`} />
        </label>
      )}

      {!isLayout && (
        <>
          <label className="block mt-2">
            <span className="text-xs font-medium text-ink-muted">Help text (optional)</span>
            <input value={field.help_text} onChange={(e) => set({ help_text: e.target.value })} className={inputCls}
                   data-testid={`${testIdPrefix}-${field.field_id}-helptext-input`} />
          </label>
          {["short_text", "long_text", "number"].includes(field.type) && (
            <label className="block mt-2">
              <span className="text-xs font-medium text-ink-muted">Placeholder (optional)</span>
              <input value={field.placeholder} onChange={(e) => set({ placeholder: e.target.value })} className={inputCls}
                     data-testid={`${testIdPrefix}-${field.field_id}-placeholder-input`} />
            </label>
          )}
        </>
      )}

      {hasOptions && (
        <div className="mt-2">
          <span className="text-xs font-medium text-ink-muted">Options</span>
          <div className="space-y-1.5 mt-1">
            {(field.options || []).map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={opt}
                  onChange={(e) => set({ options: field.options.map((o, oi) => (oi === i ? e.target.value : o)) })}
                  className={`${inputCls} mt-0`}
                  data-testid={`${testIdPrefix}-${field.field_id}-option-${i}`}
                />
                <button type="button" onClick={() => set({ options: field.options.filter((_, oi) => oi !== i) })}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`${testIdPrefix}-${field.field_id}-option-${i}-remove`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => set({ options: [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`] })}
                    className="inline-flex items-center gap-1 text-xs font-medium text-teal" data-testid={`${testIdPrefix}-${field.field_id}-add-option`}>
              <Plus className="w-3.5 h-3.5" /> Add option
            </button>
          </div>
        </div>
      )}

      {isRepeatable && (
        <div className="mt-3 pl-3 border-l-2 border-teal/30 space-y-3">
          <span className="text-xs font-medium text-ink-muted">Columns in each row</span>
          {SubRowComponent && (field.sub_fields || []).map((sub, si) => (
            <SubRowComponent
              key={sub.field_id}
              field={sub}
              index={si}
              total={field.sub_fields.length}
              typeOptions={SUB_FIELD_TYPES}
              testIdPrefix={`${testIdPrefix}-${field.field_id}-sub`}
              onUpdate={(nf) => set({ sub_fields: field.sub_fields.map((s, i) => (i === si ? nf : s)) })}
              onRemove={() => set({ sub_fields: field.sub_fields.filter((_, i) => i !== si) })}
              onMove={(from, to) => {
                const arr = [...field.sub_fields];
                const [moved] = arr.splice(from, 1);
                arr.splice(to, 0, moved);
                set({ sub_fields: arr });
              }}
            />
          ))}
          <button type="button" onClick={() => set({ sub_fields: [...(field.sub_fields || []), blankField("short_text")] })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-teal" data-testid={`${testIdPrefix}-${field.field_id}-add-subfield`}>
            <Plus className="w-3.5 h-3.5" /> Add column
          </button>
        </div>
      )}

      {!isLayout && (
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input type="checkbox" checked={!!field.required} onChange={(e) => set({ required: e.target.checked })}
                 data-testid={`${testIdPrefix}-${field.field_id}-required-checkbox`} />
          Required
        </label>
      )}
    </div>
  );
}

export function FieldEditorRow(props) {
  return <FieldEditorRowImpl {...props} SubRowComponent={SubFieldEditorRow} />;
}

function SubFieldEditorRow(props) {
  return <FieldEditorRowImpl {...props} SubRowComponent={null} />;
}
